import React from "react";
import toRupiah from "../../utils/toRupiah";
import toRibuan from "../../utils/toRibuan";
import StarIcon from "@mui/icons-material/Star";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import { useTransaksiStore } from "../../stores/transaksiStore";
import {
    Avatar,
    Box,
    Card,
    Button,
    Typography,
    Stack,
    Grid,
    Link,
    useMediaQuery,
    Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import "./CardBarang.css";
import avatarAlt from "../../utils/avatarAlt";
import { ContentGetItemResponse } from "../../constants/types";
import truncate from "../../utils/truncate";

interface ICardBarangProps {
    barang: ContentGetItemResponse;
    onOpenDetail?: (namaBarang: string, url: string, deskripsi?: string) => void;
}

const CardBarang = ({ barang, onOpenDetail = () => {} }: ICardBarangProps) => {
    const { id, namaBarang, kategori, deskripsi, harga, gambar: url, sku, stok: jumlahStok, detailBundling, favorite, hpp, hargaMultiple: multipleHarga } = barang;
    const namaKategori = kategori.nama;
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.between("xs", "sm"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
    const addToCart = useTransaksiStore((s) => s.addToCart);
    const cart = useTransaksiStore((s) => s.cart);
    const normalizedMultipleHarga = React.useMemo(
        () =>
            multipleHarga.map((li) => ({
                id: li.id,
                harga: Number(li.harga),
                keterangan: li.keterangan,
            })),
        [multipleHarga],
    );

    // Helper function untuk cek stok habis (hanya 0 atau <= 0, null dianggap tersedia)
    const isStokHabis = () => {
        // Cek stok utama: stok 0 atau kurang dari 0 (tapi tidak null)
        const stokUtamaHabis = jumlahStok !== null && jumlahStok <= 0;
        
        // Cek bundling: ada item yang stoknya 0 atau kurang (tapi tidak null)
        const bundlingHabis = detailBundling.some(
            (item) => item.stok !== null && item.stok <= 0
        );

        return stokUtamaHabis || bundlingHabis;
    };

    // Helper function untuk cek stok menipis (kurang dari 5)
    const isStokMenipis = () => {
        return jumlahStok !== null && jumlahStok < 5 && jumlahStok > 0;
    };

    return (
        <React.Fragment>
            <Card
                variant="outlined"
                sx={{
                    marginBottom: 1,
                    padding: 2,
                }}
            >
                <Box
                    display="grid"
                    gridTemplateColumns="1fr auto"
                    rowGap={isPhoneScreen ? 1 : 3}
                    sx={{
                        alignItems: { xs: "stretch", sm: "center" },
                        justifyContent: {
                            xs: "space-between",
                            sm: "space-between",
                        },
                    }}
                >
                    <Stack
                        direction={{ xs: "row-reverse", sm: "row" }}
                        sx={{
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                        }}
                        spacing={2}
                    >
                        <Box sx={{ marginBottom: favorite > 0 ? -5 : 0 }}>
                            <Link
                                onClick={() =>
                                    onOpenDetail(
                                        namaBarang,
                                        url,
                                        deskripsi,
                                    )
                                }
                                color="#000000"
                                underline="none"
                                sx={{ cursor: "pointer" }}
                            >
                                <Avatar
                                    sx={{
                                        backgroundColor: "primary.main",
                                        width: {
                                            xs: 60,
                                            sm: 60,
                                            md: 70,
                                            lg: 60,
                                            xl: 80,
                                        },
                                        height: {
                                            xs: 60,
                                            sm: 60,
                                            md: 90,
                                            lg: 70,
                                            xl: 90,
                                        },
                                    }}
                                    variant="rounded"
                                    alt={namaBarang}
                                    src={url}
                                >
                                    {avatarAlt(namaBarang)}
                                </Avatar>
                            </Link>
                            {favorite > 0 && (
                                <StarIcon
                                    sx={{
                                        width: isSmallScreen ? 32 : undefined,
                                        height: isSmallScreen ? 32 : undefined,
                                        position: "relative",
                                        zIndex: 0,
                                        bottom: {
                                            xl: 105,
                                            lg: 86,
                                            sm: 70,
                                            xs: 70,
                                        },
                                        left: "70%",
                                        border: 1,
                                        borderColor: "#e7e5e4",
                                        borderRadius: "50%",
                                        padding: isPhoneScreen ? 0.5 : 0.25,
                                        color: "#fbbf24",
                                        bgcolor: "#FFFFFF",
                                    }}
                                    fontSize="large"
                                />
                            )}
                        </Box>
                        <Grid container>
                            <Grid item>
                                <Link
                                    onClick={() =>
                                        onOpenDetail(namaBarang, url, deskripsi)
                                    }
                                    color="#000000"
                                    underline="none"
                                    sx={{ cursor: "pointer" }}
                                >
                                    <Tooltip title={namaBarang}>
                                        <Typography
                                            fontSize={isPhoneScreen ? 14 : 16}
                                            fontWeight="bold"
                                        >
                                            {truncate(namaBarang, 20)}
                                        </Typography>
                                    </Tooltip>
                                    {deskripsi ? (
                                        <Tooltip title={deskripsi}>
                                            <Typography
                                                fontSize={12}
                                                color="#76747C"
                                            >
                                                {truncate(deskripsi, 30)}
                                            </Typography>
                                        </Tooltip>
                                    ) : undefined}
                                    <Typography
                                        mt={isPhoneScreen ? undefined : 1}
                                        fontSize={12}
                                        color={
                                            isStokMenipis() ||
                                            (detailBundling.length > 0 && 
                                             detailBundling.some(
                                                (item) => item.stok !== null && item.stok <= 0
                                             ))
                                                ? "#ef4444"
                                                : "#76747C"
                                        }
                                        fontWeight={
                                            isStokMenipis() ||
                                            (detailBundling.length > 0 && 
                                             detailBundling.some(
                                                (item) => item.stok !== null && item.stok <= 0
                                             ))
                                                ? 700
                                                : 600
                                        }
                                    >
                                        {detailBundling.length > 0
                                            ? detailBundling.every(
                                                  (item) =>
                                                      item.stok === null ||
                                                      item.stok > 0,
                                              )
                                                ? "Otomatis"
                                                : "Ada barang yang habis"
                                            : jumlahStok === null
                                              ? "Tanpa stok"
                                              : jumlahStok > 5
                                                ? "Tersedia " +
                                                  toRibuan(jumlahStok) +
                                                  " barang"
                                                : jumlahStok <= 5 &&
                                                    jumlahStok > 0
                                                  ? "Barang tersisa " +
                                                    toRibuan(jumlahStok)
                                                  : "Barang telah habis"}
                                    </Typography>
                                    {multipleHarga.length > 0 ? (
                                        <Typography
                                            variant="subtitle2"
                                            color="#76747C"
                                            fontWeight="bold"
                                        >
                                            {toRupiah(
                                                Number(multipleHarga[0].harga),
                                            )}
                                        </Typography>
                                    ) : (
                                        <Typography
                                            variant="subtitle2"
                                            color="#76747C"
                                            fontWeight="bold"
                                        >
                                            {toRupiah(harga)}
                                        </Typography>
                                    )}
                                </Link>
                            </Grid>
                        </Grid>
                    </Stack>
                    <Button
                        disabled={
                            cart.findIndex((c) => c.id === id) >= 0 ||
                            isStokHabis()
                        }
                        data-testid="button-pesan"
                        variant="contained"
                        onClick={() =>
                            addToCart({
                                id,
                                namaBarang,
                                namaKategori,
                                harga:
                                    multipleHarga.length > 0
                                        ? Number(multipleHarga[0].harga)
                                        : harga,
                                url,
                                qty: 1,
                                sku,
                                jumlahStok,
                                detailBundling,
                                hpp,
                                faktur: [],
                                multipleHarga: normalizedMultipleHarga,
                            })
                        }
                        startIcon={
                            cart.findIndex((c) => c.id === id) >= 0 ? (
                                <ShoppingCartIcon />
                            ) : (
                                <AddShoppingCartIcon />
                            )
                        }
                        sx={{
                            gridColumn: isPhoneScreen ? "1 / 3" : "unset",
                        }}
                    >
                        {cart.findIndex((c) => c.id === id) >= 0
                            ? "Di Keranjang"
                            : isStokHabis()
                              ? "Stok Habis"
                              : "Pesan"}
                    </Button>
                </Box>
            </Card>
        </React.Fragment>
    );
};

export default React.memo(CardBarang);
